import { useState } from 'react';

import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { Route } from 'react-router-dom';

import { parseErrorMessageFromPointerUK } from '../../../helpers/parseErrorMessageFromPointer/parseErrorMessageFromPointerUK';
import { useCurrentStep } from '../../../hooks/useCurrentStep';
import { useSignup } from '../../../hooks/useSignup';
import { useSignupParams } from '../../../hooks/useSignupParams';
import { TStateUpdateErrorUK } from '../../../types';
import CheckEligibilityUK from '../../CheckEligibility/CheckEligibilityUK/CheckEligibilityUK';
import CompanyDetailsUK from '../../CompanyDetails/CompanyDetailsUK/CompanyDetailsUK';
import CheckSkippedDecision from '../../Decision/CheckSkippedDecision/CheckSkippedDecision';
import Decision from '../../Decision/Decision';
import GetStartedWithPrefillUK from '../../GetStarted/GetStartedWithPrefillUK/GetStartedWithPrefillUK';
import OffersWithOffersCheckUK from '../../Offers/OffersWithOffersCheckUK';
import PersonalDetailsCheckWithOffersCheck from '../../PersonalDetails/PersonalDetailsUK/PersonalDetailsCheck/PersonalDetailsCheckWithOffersCheck';
import PersonalDetailsUK from '../../PersonalDetails/PersonalDetailsUK/PersonalDetailsUK';
import RouteSpinner from '../../RouteSpinner/RouteSpinner';
import TradingHistoryUK from '../../TradingHistory/TradingHistoryUK/TradingHistoryUK';
import StateErrorModal from '../StateErrorModal/StateErrorModal';

type TRoutes = {
  path: string;
  element: JSX.Element;
  key: string;
}[];

const SignupRoutesUK = () => {
  const [stateError, setStateError] = useState<TStateUpdateErrorUK>(null);

  const { isLoading, hasAllData } = useSignup();
  const { isPartnerCustomer } = useSignupParams();
  const currentStep = useCurrentStep();

  const stepIsNewOrLoading =
    (isLoading || !hasAllData) && currentStep !== 'new';

  if (stepIsNewOrLoading) {
    return <RouteSpinner />;
  }

  const handleStateUpdateError = (error: Error) => {
    const errorMessage = parseErrorMessageFromPointerUK(error);
    setStateError(errorMessage);
  };

  const defaultRoutes: TRoutes = [
    {
      path: '/new',
      element: (
        <GetStartedWithPrefillUK
          handleStateUpdateError={handleStateUpdateError}
        />
      ),
      key: 'get-started',
    },
    {
      path: '/business',
      element: (
        <CompanyDetailsUK handleStateUpdateError={handleStateUpdateError} />
      ),
      key: 'business',
    },
    {
      path: '/check-eligibility',
      element: <CheckEligibilityUK />,
      key: 'check-eligibility',
    },
    {
      path: '/applicant',
      element: (
        <PersonalDetailsUK handleStateUpdateError={handleStateUpdateError} />
      ),
      key: 'applicant',
    },
    {
      path: '/offers',
      element: <OffersWithOffersCheckUK />,
      key: 'offers',
    },
    {
      path: '/check',
      element: <PersonalDetailsCheckWithOffersCheck />,
      key: 'check',
    },
    {
      path: '/documents',
      element: <TradingHistoryUK />,
      key: 'trading-history',
    },
  ];

  const partnerRoutes: TRoutes = [
    {
      path: '/check-skipped',
      element: <CheckSkippedDecision />,
      key: 'check-skipped',
    },
    { path: '/decision', element: <Decision />, key: 'decision' },
  ];

  return (
    <>
      <Routes>
        {defaultRoutes.map((route) => (
          <Route key={route.key} path={route.path} element={route.element} />
        ))}
        {isPartnerCustomer &&
          partnerRoutes.map((route) => (
            <Route key={route.key} path={route.path} element={route.element} />
          ))}

        {/* Fallback route */}
        <Route path="/*" element={<RouteSpinner />} />
      </Routes>
      <StateErrorModal
        stateError={stateError}
        handleClose={() => setStateError(null)}
      />
    </>
  );
};

export default SignupRoutesUK;
